import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { useT } from '../lib/i18n.tsx';
import { InfoPopover } from './InfoPopover.tsx';
import { Table, TableScroll, Td, Th } from './ui/table.tsx';

export interface Column<Row> {
  id: string;
  /** Column title as text (used for the sort button label) and shown in the header. */
  header: string;
  /** Explanation of the column (for example its attribution rule), shown in an info popover. */
  help?: ReactNode;
  cell: (row: Row) => ReactNode;
  /** Makes the column sortable; `null` values sort last. */
  sortValue?: (row: Row) => number | string | null;
  align?: 'left' | 'right';
}

export interface TotalsRow {
  label: string;
  cells: Record<string, ReactNode>;
}

interface Sort {
  id: string;
  direction: 'asc' | 'desc';
}

function compare(
  a: number | string | null,
  b: number | string | null,
  direction: 'asc' | 'desc',
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // missing values always last
  if (b === null) return -1;
  const order =
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b));
  return direction === 'asc' ? order : -order;
}

/**
 * The one table of the dashboard: sortable columns (with `aria-sort`), numbers right-aligned in
 * tabular figures, a sticky header and first column, a totals row, and sideways scrolling on narrow
 * screens. The caption is for screen readers.
 */
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  defaultSort,
  totals,
  className,
}: {
  caption: string;
  columns: readonly Column<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  defaultSort?: Sort;
  totals?: TotalsRow;
  className?: string;
}) {
  const t = useT();
  const [sort, setSort] = useState<Sort | null>(defaultSort ?? null);

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.id === sort?.id);
    if (!sort || !column?.sortValue) return rows;
    const value = column.sortValue;
    return [...rows].sort((a, b) => compare(value(a), value(b), sort.direction));
  }, [rows, columns, sort]);

  const toggle = (column: Column<Row>): void => {
    setSort((current) => {
      if (current?.id !== column.id)
        return { id: column.id, direction: column.align === 'right' ? 'desc' : 'asc' };
      return { id: column.id, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  return (
    <TableScroll className={className}>
      <Table>
        <caption className="sr-only">{caption}</caption>
        <thead className="sticky top-0 z-10">
          <tr>
            {columns.map((column, index) => {
              const active = sort?.id === column.id;
              const SortIcon = !active
                ? ArrowUpDown
                : sort.direction === 'asc'
                  ? ArrowUp
                  : ArrowDown;
              return (
                <Th
                  key={column.id}
                  scope="col"
                  aria-sort={
                    active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  className={cn(
                    column.align === 'right' && 'text-right',
                    index === 0 && 'sticky left-0 z-20 bg-muted',
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex items-center gap-1',
                      column.align === 'right' && 'flex-row-reverse',
                    )}
                  >
                    {column.sortValue ? (
                      <button
                        type="button"
                        onClick={() => toggle(column)}
                        aria-label={t.common.sortBy(column.header)}
                        className="inline-flex items-center gap-1 rounded-sm font-semibold hover:text-foreground"
                      >
                        {column.header}
                        <SortIcon aria-hidden="true" className="size-3" />
                      </button>
                    ) : (
                      column.header
                    )}
                    {column.help ? (
                      <InfoPopover label={column.header}>{column.help}</InfoPopover>
                    ) : null}
                  </span>
                </Th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={rowKey(row)} className="hover:bg-muted/50">
              {columns.map((column, index) =>
                index === 0 ? (
                  <Th
                    key={column.id}
                    scope="row"
                    className="sticky left-0 z-10 bg-card align-middle text-sm font-medium text-foreground"
                  >
                    {column.cell(row)}
                  </Th>
                ) : (
                  <Td
                    key={column.id}
                    className={cn(column.align === 'right' && 'text-right tabular-nums')}
                  >
                    {column.cell(row)}
                  </Td>
                ),
              )}
            </tr>
          ))}
        </tbody>
        {totals ? (
          <tfoot>
            <tr className="bg-muted font-semibold">
              {columns.map((column, index) => (
                <Td
                  key={column.id}
                  className={cn(
                    'border-t-2',
                    column.align === 'right' && 'text-right tabular-nums',
                    index === 0 && 'sticky left-0 bg-muted',
                  )}
                >
                  {index === 0 ? totals.label : (totals.cells[column.id] ?? null)}
                </Td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </Table>
    </TableScroll>
  );
}
